import { useState, useEffect } from 'react';
import {
  Button,
  ButtonSet,
  SkeletonPlaceholder,
  Toggle,
  ContentSwitcher,
  Switch,
  TextInput,
  InlineNotification,
} from 'carbon-components-react';
import { FormModal } from 'src/common/utils/FormModal';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from '../locales/messages';
import Cookies from 'js-cookie';
import Helmet from 'react-helmet';

import './APIConnectPriceEstimator.scss';
import '../../../common/style/importantStyle.scss';
import '../../../common/style/common.scss';
import countryList from '../services/currencies.json';
import { segmentTracking } from '../lib/tracker';
import Footnote from './Footnote/Footnote';
import Thanks from './Thanks/Thanks';
import { useEnv } from '../../../common/hooks/widgets/useEnv';
import { useBPName } from '../../../common/hooks/widgets/useBPName';
import { useCookiesURL } from '../../../common/hooks/widgets/useCookiesURL';
import { usePrice } from '../hooks/usePrice';
import {
  ADVANCED,
  BASE,
  REGEX,
  RESERVED_INSTANCE,
  SOFTWARE,
  UT30APIConnect,
  emailCOSJson,
  pageUrl,
  productTitle,
} from '../constants/spread';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import * as URX_FORM from '../constants/urx-form';
import { LangDefaults, useLang } from 'src/common/hooks/widgets/useLang';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import {
  urxScript,
  urxScriptLoader,
} from 'src/common/hooks/widgets/globalDefinitions';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import Feedback from 'src/common/utils/Feedback';
import {
  ADVANCED_DEFAULT,
  ADVANCED_MAX,
  ADVANCED_MIN,
  ADVANCED_TIER_100M,
  ADVANCED_TIER_1200M,
  ADVANCED_TIER_2400M,
  ADVANCED_TIER_250M,
  ADVANCED_TIER_500M,
  ADVANCED_TIER_750M,
  BASE_DEFAULT,
  BASE_MAX,
  BASE_MIN,
  BASE_UNIT_100K,
} from '../constants/prices';
import { useNumberFormat } from 'src/common/hooks/widgets/useNumberFormat';
import DisclaimerText from 'src/common/components/DisclaimerText/DisclaimerText';

export interface APIConnectPriceEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const APIConnectPriceEstimator = ({
  langCode,
  test,
  countryToTest,
}: APIConnectPriceEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');
  // lang and userlocale is based out of ddo
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );
  /* Have urx form deault in us-en when on language us page */
  const [urxlang] = useState(localeCode.toString());
  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);
  const [environment] = useEnv();
  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);

  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const intl = useIntl();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { bpName, logo, companyId, desc, mssp } = useBPName(
    UT30APIConnect,
    true,
  );
  const cookieURL = useCookiesURL();
  const [countDownPassed, setCountDownPassed] = useState(false);
  const [formComplete, setFormComplete] = useState(false);
  const [bpConsentMail, setBpConsentMail] = useState(false);

  //api-connect
  const [offering, setOffering] = useState(BASE);
  const [offeringIndex, setOfferingIndex] = useState(0);
  const [advanced, setAdvanced] = useState(false);
  const [volume, setVolume] = useState(
    offering === BASE ? BASE_DEFAULT : ADVANCED_DEFAULT,
  );
  const [formattedVolume] = useNumberFormat(volume);
  const [volumeRoundedUp, setVolumeRoundedUp] = useState(
    offering === BASE ? BASE_DEFAULT : ADVANCED_DEFAULT,
  );
  const [formattedVolumeRoundedUp] = useNumberFormat(volumeRoundedUp);
  const [formattedBaseMax] = useNumberFormat(BASE_MAX);
  const [formattedAdvancedMax] = useNumberFormat(ADVANCED_MAX);
  const [units, setUnits] = useState(1);
  const [tier, setTier] = useState(<></>);
  const [advancedFootNote, setAdvancedFootNote] = useState(
    <FormattedMessage {...messages.api_call_static_message_advanced} />,
  );
  const [maxVolume, setMaxVolume] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [price, basePrice, advancedPrice] = usePrice({
    currency,
    localeCode,
    country,
    offering,
    units,
  });

  useEffect(() => {
    setTimeout(() => {
      setCountDownPassed(!countDownPassed);
    }, 1000);
  }, []);

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
  };

  useEffect(() => {
    setCookie();
  }, [offering, volume]);

  const setCookie = () => {
    const mailCheckbox =
      document.querySelector<HTMLInputElement>('#Q_MAIL_CONSENT');
    setBpConsentMail(!!mailCheckbox?.checked); // set false if unchecked or if undefined
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            ' Offerings: ' +
            offering +
            ' | API Call volume per year: ' +
            formattedVolume +
            ' | Private Data Connectivity: ' +
            advanced +
            ' | API Call Volume per year: ' +
            formattedVolumeRoundedUp +
            ' | Tier to purchase: ' +
            tier?.props?.defaultMessage +
            ' | Units: ' +
            units +
            (getBp() ? `|Preferred BP:${getBp()}` : ''),
          Q_ASSET_URL: window.location.href, // ISC - Asset URL initial
          Q_ASSET_NAME: 'API Connect Price Estimator', // ISC - Asset Name
        },
      }),
    );

    console.log(Cookies.get('urxdcq'));
  };

  const getBp = () => {
    if (bpName) return bpName;
    if (logo) return companyId;
    if (desc) return desc;
    if (mssp) return mssp;

    const bpField = document.querySelector('#Q_BPNAME') as HTMLInputElement;
    return bpField ? bpField.value : '';
  };

  const calculateBaseRoundUpUnits = (baseVolume: number) => {
    const units = baseVolume / BASE_UNIT_100K;
    const roundedUpUnits =
      units < 1 ? 1 : Math.round(baseVolume / BASE_UNIT_100K);
    return baseVolume === 0 ? 0 : roundedUpUnits;
  };

  const determineUnits = (inputVolume: number, offering: string) => {
    if (offering === BASE) {
      determineUnitsForBase(inputVolume);
    } else {
      determineTiersForAdvanced(inputVolume);
    }
  };

  const determineUnitsForBase = (inputVolumes: number) => {
    const units = calculateBaseRoundUpUnits(inputVolumes);
    const roundedUpVolumes = units * BASE_UNIT_100K;
    setVolumeRoundedUp(roundedUpVolumes);
    setUnits(units);
  };

  const determineTiersForAdvanced = (inputVolumes: number) => {
    if (inputVolumes > 0) {
      switch (true) {
        case inputVolumes <= ADVANCED_TIER_100M:
          setAdvancedFootNote(
            <FormattedMessage
              {...messages.api_call_dynamic_message_advanced_100M}
            />,
          );
          setTier(<FormattedMessage {...messages.tier_100M} />);
          setUnits(100);
          break;
        case inputVolumes <= ADVANCED_TIER_250M:
          setAdvancedFootNote(
            <FormattedMessage
              {...messages.api_call_dynamic_message_advanced_250M}
            />,
          );
          setTier(<FormattedMessage {...messages.tier_250M} />);
          setUnits(250);
          break;
        case inputVolumes <= ADVANCED_TIER_500M:
          setAdvancedFootNote(
            <FormattedMessage
              {...messages.api_call_dynamic_message_advanced_500M}
            />,
          );
          setTier(<FormattedMessage {...messages.tier_500M} />);
          setUnits(500);
          break;
        case inputVolumes <= ADVANCED_TIER_750M:
          setAdvancedFootNote(
            <FormattedMessage
              {...messages.api_call_dynamic_message_advanced_750M}
            />,
          );
          setTier(<FormattedMessage {...messages.tier_750M} />);
          setUnits(750);
          break;
        case inputVolumes <= ADVANCED_TIER_1200M:
          setAdvancedFootNote(
            <FormattedMessage
              {...messages.api_call_dynamic_message_advanced_1200M}
            />,
          );
          setTier(<FormattedMessage {...messages.tier_1200M} />);
          setUnits(1200);
          break;
        case inputVolumes <= ADVANCED_TIER_2400M:
          setAdvancedFootNote(
            <FormattedMessage
              {...messages.api_call_dynamic_message_advanced_2400M}
            />,
          );
          setTier(<FormattedMessage {...messages.tier_2400M} />);
          setUnits(2400);
          break;
        default:
          setAdvancedFootNote(
            <FormattedMessage {...messages.api_call_static_message_advanced} />,
          );
          setTier(<></>);
          break;
      }
    } else {
      setUnits(0);
      setAdvancedFootNote(
        <FormattedMessage {...messages.api_call_static_message_advanced} />,
      );
      setTier(<></>);
    }
  };

  const checkForMaxValue = (
    volume: number,
    setMax: React.Dispatch<React.SetStateAction<boolean>>,
    maxValue: number,
    invalidStatus: boolean,
  ) => {
    if (!invalidStatus) {
      if (volume > maxValue) {
        setMax(true);
      } else {
        setMax(false);
      }
    } else {
      setMax(true);
    }
  };

  const initReservedAndSoftware = (selectedOffering: string) => {
    setOffering(selectedOffering);
    setOfferingIndex(
      selectedOffering === RESERVED_INSTANCE
        ? 2
        : selectedOffering === SOFTWARE
        ? 3
        : 0,
    );
    setMaxVolume(false);
    setAdvanced(false);
    setVolume(0);
    setUnits(0);
    setVolumeRoundedUp(0);
    setTier(<></>);
  };

  const initBaseOffering = () => {
    setOffering(BASE);
    setOfferingIndex(0);
    setMaxVolume(false);
    setAdvanced(false);
    setVolume(BASE_DEFAULT);
    setVolumeRoundedUp(BASE_DEFAULT);
    setUnits(1);
  };
  /**
   * Read and set defaults from BVA
   */
  const startTxnProcessor = () => {
    try {
      setTimeout(async () => {
        const showBenefit = localStorage.getItem('showForm');
        if (showBenefit !== null) {
          if (showBenefit === 'true') {
            setShowForm(true);
          }
        }
        startTxnProcessor();
      }, 1000);
    } catch (err) {
      console.error(`Error while timeout: ${err}`);
    }
  };

  startTxnProcessor();

  useEffect(() => {
    const showBenefit = localStorage.getItem('showForm');
    if (showBenefit === 'true') {
      initUrxForm(
        environment,
        URX_FORM.INSTANCE_ID,
        URX_FORM.FORM_ID,
        urxlang as IBMLocale,
        companyId,
      );
      setTimeout(
        async () => {
          setCookie();
          setDrawerOpen(true);
          setShowForm(false);
          localStorage.setItem('showForm', 'false');
        },
        bpName ? 1000 : 10,
      );
    }
  }, [showForm]);

  const initAdvancedOffering = () => {
    setOffering(ADVANCED);
    setOfferingIndex(1);
    setAdvanced(true);
    setMaxVolume(false);
    setVolume(ADVANCED_DEFAULT);
    setTier(<FormattedMessage {...messages.tier_100M} />);
    setAdvancedFootNote(
      <FormattedMessage {...messages.api_call_static_message_advanced} />,
    );
    setVolumeRoundedUp(0);
    setUnits(100);
  };

  return (
    <div className="api-connect widget-styles">
      <Helmet>
        <script src={urxScript}></script>
        <script src={urxScriptLoader}></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          bpName={bpName}
          advanced={advanced}
          price={price as string}
          bpConsentMail={bpConsentMail}
          offerings={offering}
          tier={tier}
          volume={volume}
          volumeRoundUp={volumeRoundedUp}
        />
      ) : (
        <div className="bx--grid" id="api-connect-grid">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          <div className="bx--row mar3">
            {/* Main content div */}
            <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-4">
              {/* Starting heading */}
              <div className="bx--row">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                  <p className="page_sub_heading">
                    <FormattedMessage {...messages.heading_one} />
                  </p>
                </div>
              </div>
              {/* offering header with dynamic tooltip content */}
              <div className="bx--row mar3">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                  <p className="page_body_2">
                    <FormattedMessage {...messages.heading_two} />
                  </p>
                </div>
              </div>
              {/* ContentSwitcher for offerings */}
              <div className="bx--row">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                  <ContentSwitcher
                    className="content-switcher"
                    selectedIndex={offeringIndex}
                    onChange={(obj) => {
                      const { text } = obj;
                      if (text === ADVANCED) {
                        initAdvancedOffering();
                      }
                      if (text === BASE) {
                        initBaseOffering();
                      }
                      if (text === RESERVED_INSTANCE || text === SOFTWARE) {
                        initReservedAndSoftware(text);
                      }
                      segmentTracking('CTA Clicked', {
                        pageTitle: 'API Connect Landing Page',
                        object: 'Pricing page',
                        resultValue: 'CTA clicked',
                        CTA: 'API_C Offering selected',
                        location: 'Estimator',
                        action: '',
                        field: '',
                      });
                    }}
                  >
                    <Switch name={BASE} text={BASE} />
                    <Switch name={ADVANCED} text={ADVANCED} />
                    <Switch name={ADVANCED} text={RESERVED_INSTANCE} />
                    <Switch name={SOFTWARE} text={SOFTWARE} />
                  </ContentSwitcher>
                </div>
              </div>
              {offering === BASE || offering === ADVANCED ? (
                <div id="basic-advanced">
                  {/* For Basic & Advanced */}
                  {/* Data Connectivity label */}
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_body_2">
                        <FormattedMessage
                          {...messages.advanced_data_connectivity}
                        />
                      </p>
                    </div>
                  </div>
                  {/* Data Connectivity Toggle */}
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Toggle
                        labelA={intl.formatMessage(messages.no)}
                        labelB={intl.formatMessage(messages.yes)}
                        id="advanced-toggle-id"
                        toggled={advanced}
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'API Connect Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Private Data Connectivity Toggled',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          setAdvanced(!advanced);
                          advanced
                            ? initBaseOffering()
                            : initAdvancedOffering();
                        }}
                      />
                    </div>
                  </div>
                  {/* API call volume header e */}
                  <div className="bx--row mar3">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_body_2">
                        <FormattedMessage {...messages.api_call_heading} />
                      </p>
                    </div>
                  </div>
                  {/* API call volume header with Number Input box */}
                  <div className="bx--row mt-5">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <TextInput
                        className="bx--text-input bx--text-input--lg"
                        id="api-call-volume"
                        invalidText={
                          offering === BASE
                            ? `Please enter number between ${BASE_MIN} - ${formattedBaseMax}.`
                            : `Please enter number between ${ADVANCED_MIN} - ${formattedAdvancedMax}.`
                        }
                        hideLabel={true}
                        value={formattedVolume}
                        invalid={maxVolume}
                        onChange={(e) => {
                          const element = e.currentTarget as HTMLInputElement;
                          const value = element.value.replaceAll(',', '');
                          const invalid =
                            !REGEX.test(value) && value.length > 0;
                          const inputVolume = parseInt(value);
                          checkForMaxValue(
                            inputVolume,
                            setMaxVolume,
                            offering === BASE ? BASE_MAX : ADVANCED_MAX,
                            invalid,
                          );
                          setVolume(inputVolume);
                          determineUnits(inputVolume, offering);

                          // For segment tracking
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'API Connect Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'API_C api call volumes',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        labelText={
                          <FormattedMessage {...messages.api_call_heading} />
                        }
                      />
                    </div>
                  </div>
                  {/* API call volume dynamic lable */}
                  <div className="bx--row mt-5">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_body_2">
                        {offering === BASE ? (
                          <FormattedMessage
                            {...messages.api_call_message_basic}
                          />
                        ) : (
                          advancedFootNote
                        )}
                      </p>
                    </div>
                  </div>
                  {/* Legal Disclaimer */}
                  <div id="footnote" className="div-only-l mar3">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 marginleft_1">
                        <div>
                          <p>
                            <span className="footnote_num">1</span>
                          </p>
                        </div>
                        <div className="footnote_text">
                          <DisclaimerText
                            localeCode={localeCode}
                            product={'API Connect'}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* <div id="footnote" className="div-only-lg mar3">
                    <div className="bx--row">
                      <div className="bx--col-lg-16">
                        <Footnote numNote={1} />
                        <div className="footnote_text">
                          <FormattedMessage {...messages.footnoteOne} />
                        </div>
                      </div>
                    </div>
                  </div> */}
                </div>
              ) : (
                <div id="reserved-software">
                  {/* For Reserved & Software */}
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_body_2">
                        <FormattedMessage
                          {...messages.heading_reserved_software}
                        />
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* For Feedback section */}
              <div className="mar3">
                <Feedback
                  pageTitle="API Connect Pricing Page"
                  object="Pricing Page"
                  messages={messages}
                  productTitle={productTitle}
                  pageUrl={pageUrl}
                  emailJson={emailCOSJson}
                ></Feedback>
              </div>
            </div>
            {/* Separator div */}
            <div className="bx--col-lg-2 bx--col-md-16 bx--col-sm-16"></div>
            {/* Pricing/Greybox div */}
            <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16 summary-section">
              {offering !== SOFTWARE ? (
                <div className="bx--row">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 summary-inline-notification">
                    <InlineNotification
                      className="lg-align"
                      kind="info"
                      role="onEDREnterprise"
                      title={offering}
                      subtitle={
                        offering === BASE ? (
                          <FormattedMessage {...messages.tooltip_base} />
                        ) : offering === ADVANCED ? (
                          <FormattedMessage {...messages.tooltip_advanced} />
                        ) : offering === RESERVED_INSTANCE ? (
                          <FormattedMessage {...messages.tooltip_reserved} />
                        ) : null
                      }
                      hideCloseButton={true}
                    />
                  </div>
                </div>
              ) : null}
              <div className="bx--row">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 summary-pricing">
                  {/* Table Header */}
                  <div className="bx--row">
                    <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body bold">
                        <FormattedMessage {...messages.configuration} />
                      </p>
                    </div>
                    <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body bold slide-left">
                        <FormattedMessage {...messages.value} />
                      </p>
                    </div>
                  </div>
                  {/* Summary Data Connectiviity */}
                  {(offering === BASE || offering === ADVANCED) &&
                  !maxVolume ? (
                    <div>
                      <div className="bx--row">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                          <hr className="api-connect__gray-hr" />
                        </div>
                      </div>
                      <div className="bx--row">
                        <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                          <p className="price_table_comp__requirement price_table_comp_color">
                            <FormattedMessage
                              {...messages.summary_data_connectivity}
                            />
                          </p>
                        </div>
                        <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                          <p className="page_body_2 slide-left">
                            {advanced ? (
                              <FormattedMessage {...messages.yes} />
                            ) : (
                              <FormattedMessage {...messages.no} />
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {/* Summary Offering Selected */}
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <hr className="api-connect__gray-hr" />
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                      <p className="price_table_comp__requirement price_table_comp_color">
                        <FormattedMessage
                          {...messages.summary_offer_selecting}
                        />
                      </p>
                    </div>
                    <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body_2 slide-left">{offering}</p>
                    </div>
                  </div>
                  {/* Summary Bucket to Purchase */}
                  {offering === BASE && !maxVolume ? (
                    <div>
                      <div className="bx--row">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                          <hr className="api-connect__gray-hr" />
                        </div>
                      </div>
                      <div className="bx--row">
                        <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                          <p className="price_table_comp__requirement price_table_comp_color">
                            <FormattedMessage
                              {...messages.api_call_rounded_up}
                            />
                          </p>
                        </div>
                        <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                          <p className="page_body_2 slide-left">
                            {formattedVolumeRoundedUp}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : offering === ADVANCED && !maxVolume ? (
                    <div>
                      <div className="bx--row">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                          <hr className="api-connect__gray-hr" />
                        </div>
                      </div>
                      <div className="bx--row">
                        <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                          <p className="price_table_comp__requirement price_table_comp_color">
                            <FormattedMessage
                              {...messages.summary_bucket_to_volume}
                            />
                          </p>
                        </div>
                        <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                          <p className="page_body_2 slide-left">{tier}</p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {/* Summary Api Call Volume */}
                  {(offering === BASE || offering === ADVANCED) &&
                  !maxVolume ? (
                    <div>
                      <div className="bx--row">
                        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                          <hr className="api-connect__gray-hr" />
                        </div>
                      </div>
                      <div className="bx--row">
                        <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                          <p className="price_table_comp__requirement price_table_comp_color">
                            <FormattedMessage
                              {...messages.summary_api_call_volume}
                            />
                          </p>
                        </div>
                        <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                          <p className="page_body_2 slide-left">
                            {formattedVolume}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <hr className="api-connect__gray-hr" />
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      {(offering === BASE || offering === ADVANCED) &&
                      !maxVolume ? (
                        <>
                          <p
                            id="widget.estimated-cost"
                            className="api-connect__ibm-type-e price_table_comp__bodycopy padding_24_2"
                          >
                            <FormattedMessage
                              {...messages.estimated_monthly_cost}
                            />
                            <sup className="api-connect__sup">1</sup>
                          </p>
                          <p className="padding_8 price_table_comp__price">
                            {basePrice === 0 || advancedPrice === 0 ? (
                              <SkeletonPlaceholder className="api-connect__sticky-nav-skeleton" />
                            ) : (
                              `${price} *`
                            )}
                          </p>
                        </>
                      ) : (
                        <p className="padding_8 price_table_comp__price padding_24_2">
                          <FormattedMessage
                            {...messages.contact_us_monthy_cost}
                          />
                          {`*`}
                        </p>
                      )}
                      <p className="price_table_comp__disclaimer price_table_comp_color padding_24_1">
                        * <FormattedMessage {...messages.asteriskPrice} />
                      </p>
                      <ButtonSet className="api_connect__stack-button">
                        {(offering === BASE || offering === ADVANCED) &&
                        !maxVolume ? (
                          <Button
                            onClick={() => {
                              segmentTracking('CTA Clicked', {
                                pageTitle: 'API Connect Pricing Page',
                                object: 'Pricing Page',
                                resultValue: 'CTA clicked',
                                CTA: 'Request a quote now',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              initUrxForm(
                                environment,
                                URX_FORM.INSTANCE_ID,
                                URX_FORM.FORM_ID,
                                urxlang as IBMLocale,
                                companyId,
                              );
                              setDrawerOpen(true);
                              setCookie();
                            }}
                          >
                            <FormattedMessage {...messages.getQuoteButton} />
                          </Button>
                        ) : (
                          <Button
                            onClick={() => {
                              segmentTracking('CTA Clicked', {
                                pageTitle: 'API Connect Pricing Page',
                                object: 'Pricing Page',
                                resultValue: 'CTA clicked',
                                CTA: 'Request a quote now',
                                location: 'Estimator',
                                action: '',
                                field: '',
                              });
                              initUrxForm(
                                environment,
                                URX_FORM.INSTANCE_ID,
                                URX_FORM.FORM_ID,
                                urxlang as IBMLocale,
                                companyId,
                              );
                              setDrawerOpen(true);
                              setCookie();
                            }}
                          >
                            <FormattedMessage {...messages.button_contact_us} />
                          </Button>
                        )}
                      </ButtonSet>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <FormModal
            pageTitle="API Connect PE Page"
            object="API Connect PE Page"
            productTitle={'API Connect price estimator'}
            pageUrl={''}
            location={'Estimator'}
            bpName={bpName}
            logo={logo}
            drawerOpen={drawerOpen}
            setDrawerOpen={setDrawerOpen}
            formId={URX_FORM.FORM_ID}
            lang={lang}
          ></FormModal>
        </div>
      )}
    </div>
  );
};

export default APIConnectPriceEstimator;
